import React from "react";
import { getListQueryStatus } from "@hex-insights/app-modules";
import {
	Button,
	Conditional,
	Else,
	Heading,
	If,
	makeClassName,
	Modal,
	ModalProps,
	RequiredKeys,
	Row,
	useMediaQuery,
	useToggle,
} from "@hex-insights/core";
import { Icon } from "../../../../Components/Icon";
import {
	Student,
	StudentEnrollmentIntent,
	useAcademicYearNamesQuery,
	useStudentsWithEnrollmentIntentsListQuery,
} from "../../../../Utilities";
import { ClassYearUtilities } from "../../../Utilities";
import { ReEnrollmentDisplay } from "./Display";
import { ReEnrollmentForm } from "./Form";
import styles from "./styles.module.css";

// TODO dynamic academic year selection

export type StudentEnrollmentIntentStudentsReenrollmentModalProps = {
	studentIDs: Student["id"][];
} & RequiredKeys<Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">, "onClose">;

export function StudentEnrollmentIntentStudentsReenrollmentModal({
	studentIDs,
	ifRef,
	onClose,
	className,
	style,
}: StudentEnrollmentIntentStudentsReenrollmentModalProps) {
	const { loading, data, error } = useStudentsWithEnrollmentIntentsListQuery({
		variables: {
			filters: {
				idIn: studentIDs,
			},
			studentEnrollmentIntentFilters: {
				academicYear: [{ nameEQ: "2024-2025" }],
			},
		},
	});
	const students = React.useMemo(() => data?.studentConnection.edges.map((e) => e.node), [data]);
	const { loading: loadingAcademicYears, data: academicYearsData } = useAcademicYearNamesQuery({
		variables: {
			filters: {
				nameEQ: "2024-2025",
			},
		},
	});
	const academicYear = academicYearsData?.academicYearConnection.edges[0]?.node;

	const { loading: loadingClassYears, classYears } = ClassYearUtilities.useClassYearsMap();

	const queryStatus = getListQueryStatus({
		loading,
		data,
		error,
		resultKey: "studentConnection",
	});

	const { isEditing, startEditing, stopEditing, canStopEditing } = useEditingToggle(students);

	const isWide = useMediaQuery("(min-width: 46rem)");

	return (
		<Modal
			ifRef={ifRef}
			onClose={onClose}
			className={makeClassName(styles["modal"], isWide ? styles["modal--wide"] : "", className)}
			style={style}
		>
			<Modal.Header className={styles["modal__header"]}>
				<Row justify="spaced-start" align="center">
					<Heading.H2 noMargin>2024-2025 Enrollment</Heading.H2>

					<If condition={!isEditing}>
						<Button
							variant="tertiary"
							size="small"
							onClick={startEditing}
							className="button--primary-tertiary"
							style={{ fontWeight: "bold" }}
						>
							<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
								<Icon.PencilSimple size={16} /> Edit
							</Row>
						</Button>
					</If>
				</Row>
			</Modal.Header>
			<Conditional>
				<If condition={queryStatus === "results" && !loadingAcademicYears && !loadingClassYears}>
					<Conditional>
						<If condition={isEditing}>
							{!!students && !!academicYear && (
								<ReEnrollmentForm
									students={students}
									academicYear={academicYear}
									classYears={classYears}
									isWide={isWide}
									onCancel={canStopEditing ? stopEditing : onClose}
								/>
							)}
						</If>
						<Else>
							{!!students && <ReEnrollmentDisplay students={students} classYears={classYears} isWide={isWide} />}
						</Else>
					</Conditional>
				</If>
				<If condition={queryStatus === "loading" || loadingAcademicYears || loadingClassYears}>
					<Modal.Body>
						<span>Loading...</span>
					</Modal.Body>
				</If>
				<If condition={queryStatus === "error"}>
					<Modal.Body>
						<span>Error</span>
					</Modal.Body>
				</If>
				<If condition={queryStatus === "no-results"}>
					<Modal.Body>
						<span>No students found.</span>
					</Modal.Body>
				</If>
			</Conditional>
		</Modal>
	);
}

function useEditingToggle(
	students: { studentEnrollmentIntents: Pick<StudentEnrollmentIntent, "id">[] }[] | null | undefined,
) {
	const { isOn: isEditing, turnOn: startEditing, turnOff: stopEditing } = useToggle();
	const anyStudentsNotComplete = students?.some((student) => student.studentEnrollmentIntents.length === 0) ?? false;
	return {
		isEditing: anyStudentsNotComplete || isEditing,
		startEditing,
		stopEditing,
		canStopEditing: !anyStudentsNotComplete,
	};
}
